import { motion } from "framer-motion";
import { ArrowRight, Zap, Brain, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { EasySyncMockup } from "./features/EasySyncMockup";
import { SearchAIMockup } from "./features/SearchAIMockup";
import { PDFToolsMockup } from "./features/PDFToolsMockup";
import { HealthScoreMockup } from "./features/HealthScoreMockup";
import { ReactNode } from "react";

interface Feature {
  mockup: ReactNode;
  problem: string;
  solution: string;
  description: string;
  bgColor: string;
}

const features: Feature[] = [
  {
    mockup: <EasySyncMockup />,
    problem: "Fini les factures perdues dans vos emails",
    solution: "EasySync",
    description: "Import automatique depuis Gmail, Dropbox et 50+ organismes officiels. Vos documents arrivent sans effort.",
    bgColor: "bg-blue-500/10",
  },
  {
    mockup: <SearchAIMockup />,
    problem: "Trouvez n'importe quel document en 3 secondes",
    solution: "Recherche IA",
    description: "\"Ma facture EDF de septembre\" — une question, le bon fichier instantanément.",
    bgColor: "bg-primary/10",
  },
  {
    mockup: <PDFToolsMockup />,
    problem: "Scannez, fusionnez et signez vos PDF",
    solution: "Outils PDF",
    description: "Scanner intelligent, fusion multi-pages, signature électronique. Tout depuis votre téléphone.",
    bgColor: "bg-violet-500/10",
  },
  {
    mockup: <HealthScoreMockup />,
    problem: "Ne ratez plus jamais une échéance importante",
    solution: "Santé Administrative",
    description: "Score de conformité, alertes expiration (CNI, assurance, impôts...) — on vous prévient avant.",
    bgColor: "bg-emerald-500/10",
  },
];

export const UnifiedFeatures = () => {
  return (
    <section id="features" className="py-16 md:py-24 px-6 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent pointer-events-none" />
      
      <div className="container mx-auto max-w-5xl relative z-10">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-14"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-6">
            <Zap className="w-4 h-4" />
            Comment ça marche
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            4 problèmes résolus,{" "}
            <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
              zéro effort
            </span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Tout ce dont vous avez besoin pour reprendre le contrôle de vos documents
          </p>
        </motion.div>

        {/* Features Grid - Chess Layout */}
        <div className="space-y-6 md:space-y-8">
          {features.map((feature, index) => {
            const isReversed = index % 2 === 1;
            
            return (
              <motion.div
                key={feature.solution}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className={`
                  flex flex-col ${isReversed ? 'md:flex-row-reverse' : 'md:flex-row'} 
                  items-center gap-6 md:gap-10 
                  p-6 md:p-8 rounded-3xl 
                  backdrop-blur-xl bg-white/60 dark:bg-white/5 
                  border border-white/30 dark:border-white/10 
                  shadow-xl shadow-black/5
                  hover:shadow-2xl hover:-translate-y-1 transition-all duration-300
                `}
              >
                {/* Mockup */}
                <div className="flex-shrink-0">
                  {feature.mockup}
                </div>
                
                {/* Content */}
                <div className={`flex-1 text-center ${isReversed ? 'md:text-right' : 'md:text-left'}`}>
                  {/* Problem - bold and prominent */}
                  <h3 className="text-xl md:text-2xl font-bold mb-2 text-foreground">
                    {feature.problem}
                  </h3>
                  
                  {/* Solution badge */}
                  <div className={`
                    inline-flex items-center gap-2 px-3 py-1 rounded-full 
                    ${feature.bgColor} text-sm font-medium mb-3
                  `}>
                    <Brain className="w-3.5 h-3.5" />
                    {feature.solution}
                  </div>
                  
                  {/* Description */}
                  <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center mt-12"
        >
          <Link to="/auth?mode=signup">
            <Button variant="glow" size="lg" className="group">
              Essayer gratuitement
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
          <p className="text-sm text-muted-foreground mt-3">
            <Shield className="w-4 h-4 inline mr-1" />
            Sans carte bancaire · Vos données restent privées
          </p>
        </motion.div>
      </div>
    </section>
  );
};
